import * as React from 'react';
import { cn } from '../../utils/cn';
import type { PriceBoxProps } from './price-box.types';

/**
 * PriceBox Component
 *
 * Displays product pricing with original price, final price, and discount percentage.
 * Perfect for e-commerce product listings and shopping carts.
 */
export const PriceBox = React.forwardRef<HTMLDivElement, PriceBoxProps>(
    (
        {
            testID,
            finalPrice,
            originalPrice,
            discountPercentage,
            containerClassName,
            finalPriceClassName,
            originalPriceClassName,
            tagContainerClassName,
            tagTextClassName,
            finalPriceSize = 'xlarge',
            originalPriceSize = 'medium',
        },
        ref
    ) => {
        const finalPriceSizeClasses = {
            xsmall: 'text-xs',
            small: 'text-sm',
            medium: 'text-base',
            large: 'text-lg',
            xlarge: 'text-xl',
            xxlarge: 'text-2xl',
        };

        const originalPriceSizeClasses = {
            xsmall: 'text-[10px]',
            small: 'text-xs',
            medium: 'text-sm',
            large: 'text-base',
            xlarge: 'text-lg',
            xxlarge: 'text-xl',
        };

        return (
            <div
                ref={ref}
                data-testid={testID || 'price-box'}
                className={cn('flex flex-col gap-1', containerClassName)}
            >
                {/* Final Price */}
                <div
                    className={cn(
                        'font-bold text-[var(--color-error-60)]',
                        finalPriceSizeClasses[finalPriceSize],
                        finalPriceClassName
                    )}
                >
                    {finalPrice}
                </div>

                {/* Original Price and Discount */}
                {originalPrice && (
                    <div className="flex items-center gap-2">
                        {/* Original Price with strikethrough */}
                        <div
                            className={cn(
                                'line-through text-[var(--color-neutral-5)]0',
                                originalPriceSizeClasses[originalPriceSize],
                                originalPriceClassName
                            )}
                        >
                            {originalPrice}
                        </div>

                        {/* Discount Tag */}
                        {discountPercentage && (
                            <div
                                className={cn(
                                    'inline-flex items-center justify-center bg-[var(--color-error-10)] px-1.5 py-0.5 rounded',
                                    tagContainerClassName
                                )}
                            >
                                <span
                                    className={cn('text-xs font-bold text-[var(--color-error-60)]', tagTextClassName)}
                                >
                                    -{discountPercentage}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
);

PriceBox.displayName = 'PriceBox';
